import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DialogTemplate, TimeElapsedModel } from '@core/models/models';
import { ScreenTitleConfigService } from '@core/services/apis/screen-title-config.service';
import { DummyService } from '@core/services/apis/dummy.service';
import { AnswerTagRequest } from '@core/models/request/title-tag-request.model';
import { DetectDeviceService } from '@core/services/common';
import { MultipleSpinnerService } from '@core/services/common/multiple-spinner.service';
import { DeviceType } from '@core/models/enums';
import { BUTTON, TITLE_SETTINGS, TOOLTIP_DIALOG } from '@core/constants';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { v4 as uuidv4 } from 'uuid';
import { typeLst } from '@core/configs/title-setting.config';
import { AnswerTagItem } from '@core/models/models/title-tag.model';
import { SAUCER_LOG_ACTION } from '@core/configs';
import { SaucerLogService } from '@core/services/saucer-logs/saucer-log.service';

@Component({
  selector: 'caremoni-answer-dialog',
  templateUrl: './answer-dialog.component.html',
  styleUrls: ['./answer-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class AnswerDialogComponent implements OnInit, OnDestroy {
  @Input() data: any[] = [];
  @Input() condition: any; 
  @Input() title: any; 
  @Input() tag: any; 

  @Output() onClosedHandler = new EventEmitter();
  @Output() onSavedAnswerDialog =new EventEmitter<any>();

  dialogTemplate: DialogTemplate = new DialogTemplate();
  answerLst: AnswerTagItem[] = [];
  isShowOptionSelect: boolean = false;
  isDisableSelect:boolean = false;
  symbolShowOptionSelect: string = '+';
  isHoverToggleOff: boolean = false;
  optionLst: any[] = [
    {option: 1, checked: false},
    {option: 2, checked: false},
    {option: 3, checked: false},
    {option: 4, checked: false},
  ];
  optionOneSelected: string = 'と一致する記録';
  optionThreeSelected: string = '含む記録';
  optionOneTypeOptions = [
    { name: 'と一致する記録', value: 'と一致する記録' },
    { name: 'と一致しない記録', value: 'と一致しない記録' },
    { name: 'を含む記録', value: 'を含む記録' },
  ];
  optionThreeTypeOptions = [
    { name: '含む記録', value: '含む記録' },
    { name: '含まない記録', value: '含まない記録' },
  ];
  isDiableOption: boolean = true;
  isDisableElapsedTime: boolean = true;
  answers: string = '';
  answersThree: string = '';
  elapsedTimeData: TimeElapsedModel[] = [];
  elapsedMaxLength: number = 10;

  TT_30 = TOOLTIP_DIALOG.TT_30
  TT_31 = TOOLTIP_DIALOG.TT_31
  BUTTON = BUTTON;

  styleDisableRow: string = '';

  private viewportResizeListener?: () => void;
  private applyLayoutTimer?: ReturnType<typeof setTimeout>;
  private deviceType: DeviceType = DeviceType.DESKTOP;
  // On iPad Safari window.innerHeight shrinks together with the keyboard,
  // so keep the largest height ever seen as the stable "keyboard closed" reference.
  private fullViewportHeight: number = 0;

  constructor(
    public config: DynamicDialogConfig,
    public screenTitleConfigService:ScreenTitleConfigService,
    public dummyService: DummyService,
    private mutiLoadingService: MultipleSpinnerService,
    private dialogService: DialogService,
    private detectDeviceService: DetectDeviceService,
    private saucerLogService: SaucerLogService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.deviceType = this.detectDeviceService.getDeviceType();
    this.dialogTemplate.header = "表示回答項目設定";
    this.dialogTemplate.styleClass = 'answer-dialog';
    this.dialogTemplate.closable = false;
    this.dialogTemplate.hasFooter = true;
    this.addElapsedTime();
    await this.bindData();
    this.logDisplayAnswerSettingView();
  }

  ngOnDestroy(): void {
    clearTimeout(this.applyLayoutTimer);
    this.stopViewportListener();
  }

  onSettingInputFocus(): void {
    if (this.deviceType === DeviceType.DESKTOP) return;

    this.stopViewportListener();

    this.fullViewportHeight = Math.max(
      this.fullViewportHeight,
      window.innerHeight,
      window.visualViewport?.height ?? 0
    );

    const applyLayout = () => {
      if (window.visualViewport && window.visualViewport.height >= this.fullViewportHeight * 0.8) return;

      const dialogEl = document.querySelector('.answer-dialog') as HTMLElement | null;
      const dialogContent = document.querySelector('.answer-dialog .p-dialog-content') as HTMLElement | null;
      if (!dialogEl || !dialogContent) return;

      dialogEl.classList.add('keyboard-open');

      const visibleHeight = window.visualViewport?.height ?? window.innerHeight;
      const headerHeight = 40;
      const maxContent = visibleHeight - 8 - headerHeight - 8;
      dialogContent.style.maxHeight = `${maxContent}px`;

      // Instant scroll ('auto'): a smooth scroll gets cancelled by iOS's own
      // viewport panning while the keyboard is still animating open, leaving
      // lower rows (added elapsed-time rows) stuck behind the keyboard.
      const scrollActive = () => {
        const activeEl = document.activeElement as HTMLElement | null;
        if (activeEl?.closest('.answer-dialog')) {
          activeEl.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      };
      requestAnimationFrame(scrollActive);
      // Re-assert once the keyboard animation has fully settled.
      setTimeout(scrollActive, 300);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', applyLayout, { once: true });
    }
    this.applyLayoutTimer = setTimeout(applyLayout, 400);

    this.viewportResizeListener = () => {
      this.fullViewportHeight = Math.max(this.fullViewportHeight, window.visualViewport!.height);
      const keyboardVisible = window.visualViewport!.height < this.fullViewportHeight * 0.8;
      if (!keyboardVisible) {
        this.resetDialogPosition();
      } else {
        // The height can flap during the keyboard animation (a reset may have
        // just undone the layout) — re-apply whenever the keyboard is visible.
        applyLayout();
      }
    };
    window.visualViewport?.addEventListener('resize', this.viewportResizeListener);
  }

  onSettingInputBlur(): void {
    if (this.deviceType === DeviceType.DESKTOP) return;
    clearTimeout(this.applyLayoutTimer);
    setTimeout(() => {
      if (document.activeElement?.closest('.answer-dialog')) return;
      this.resetDialogPosition();
      this.stopViewportListener();
    }, 150);
  }

  private stopViewportListener(): void {
    if (this.viewportResizeListener && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportResizeListener);
      this.viewportResizeListener = undefined;
    }
  }

  private resetDialogPosition(): void {
    clearTimeout(this.applyLayoutTimer);
    const dialogEl = document.querySelector('.answer-dialog') as HTMLElement | null;
    const dialogContent = document.querySelector('.answer-dialog .p-dialog-content') as HTMLElement | null;
    dialogEl?.classList.remove('keyboard-open');
    if (dialogContent) {
      dialogContent.style.maxHeight = '';
    }
  }

  async bindData(){
    try {
      if (this.condition.tag == undefined) return;
      const request: AnswerTagRequest = {
        displayName: this.condition?.displayname,
        tag: this.condition.tag,
        ttlkbn:this.condition.ttlkbn,
        ttlcd:this.condition.ttlcd,
        itemid:this.condition.itemid
      }
      this.mutiLoadingService.show();
      const answers = await this.screenTitleConfigService.getAswerTags(request);
      if (answers.status == 200) {
        this.answerLst = answers.result;
        if (this.condition.comparison)
        {
          this.displayExistingAnswers(this.condition);
        }
        else if (this.answerLst?.length > 0) {
          // this.answerLst[0].selected = 'selected'  //CKWEB-1392 ==> no selected items
           this.optionLst[0].checked = true;
        }
      }
      this.handleSelectNotSpecificValue(); //Display exsiting 指定しない
      if(!this.optionLst.some(op => op.checked)) this.optionLst[0].checked = true;
      // if(!this.optionLst[0].checked) this.styleDisableRow = 'table-row-disable';
    }
    catch
    {

    }
    finally
    {
      this.mutiLoadingService.hide();

    }
    
    //if(this.answerLst.length > 0)this.answerLst[0].selected = 'selected';
  }

  handleSelectNotSpecificValue() {
    const existingLbl = this.condition?.val;
    const parsedTag = this.getParsedTag(this.condition);

    const hasSelected = this.hasSelectedAnswers(parsedTag, existingLbl);
    const isHasNoValue =
      !hasSelected &&
      !existingLbl &&
      this.condition?.comparison === 'equal';

    this.answerLst.push({
      lbl: '',
      atag: '指定しない',
      selected: !this.condition?.comparison || isHasNoValue ? 'selected' : ''
    });

    if (!this.condition?.comparison) {
      this.isShowOptionSelect = false;
      this.symbolShowOptionSelect = '+';
    }
  }

  selectAnswer(answer: any){
    //this.clearAll();
    // if(this.optionLst[2].checked || this.optionLst[3].checked) return;
    this.clearOption()
    this.optionLst[0].checked = true;
    // this.answersThree = "";
    
    if(answer.lbl == ''){
      this.answerLst.forEach((ans: any) => {
        if(ans.lbl == ''){
          if(ans.selected == 'selected') ans.selected = '';
          else ans.selected = 'selected';
        }
        else ans.selected = '';
      });
    } else {
      this.answerLst.forEach((ans: any) => {
      if(ans.atag == answer.atag && (ans.selected == '' || ans.selected == undefined)) ans.selected = 'selected';
      else if(ans.atag == answer.atag && ans.selected == 'selected') ans.selected = '';
        if(ans.lbl == '') ans.selected = '';
      });
    }

    if(answer.atag=="指定しない" && answer.selected == 'selected')
    {
      // this.isDisableSelect=true;
       this.isShowOptionSelect=false;
       this.symbolShowOptionSelect ='+';
      return;
    } 
    this.isDiableOption = !this.optionLst[2].checked;
    this.isDisableElapsedTime = !this.optionLst[3].checked;
    this.isDisableSelect=false;
  }

  clearAll() {
    this.answerLst.forEach((ans: any) => ans.selected = '');
  }
  showTabOption(){
    this.isHoverToggleOff = true;
    if (this.answerLst.some(x => x.atag === "指定しない" && x.selected === 'selected')) {
      this.clearAll()
    }

    this.isShowOptionSelect = !this.isShowOptionSelect;
    if(this.isShowOptionSelect) {
      this.symbolShowOptionSelect = '-';
    }
    else 
    {
      this.symbolShowOptionSelect = '+';
      if(!this.optionLst.some(op => op.checked)) this.optionLst[0].checked = true;
    }
  }
  onToggleMouseMove(){
    this.isHoverToggleOff = false;
  }
  pickOption(option: any){
    this.styleDisableRow = '';
    this.optionLst.forEach((op: any) => {
      if(op.option == option) op.checked = true;
      else op.checked = false;
    });
    this.isDiableOption = !this.optionLst[2].checked;
    this.isDisableElapsedTime = !this.optionLst[3].checked;
    if(this.optionLst[2].checked || this.optionLst[3].checked){
      this.clearAll()
      // this.styleDisableRow = 'table-row-disable';
      // this.answerLst.forEach((ans: any) => { ans.selected = '' });
    }
  }
  saveAns(){
    let answers: AnswerTagItem[] = [];

    if (this.answerLst.some(x => x.atag === "指定しない" && x.selected === 'selected')) {
      this.clearOption()
    }

    this.answerLst.forEach((ans: AnswerTagItem) => {
      if (ans.selected === 'selected') {
        answers.push({
          atag: ans.atag,
          lbl: ans.lbl
        });
      }
    });
    const dataSave = {
      type: '',
      value: '',
      ans: answers,
      displayname: this.title.displayname,
      elapsedTimes: [] as TimeElapsedModel[],
      condition:this.condition,
      colname: this.title?.colname,
    }
    if(this.optionLst[0].checked) dataSave.type = this.optionOneSelected;
    else if(this.optionLst[1].checked) dataSave.type = '回答があれば連携';
    else if(this.optionLst[2].checked){
      dataSave.type = this.optionThreeSelected;
      let ansLikes: AnswerTagItem[] = [];
      const ansLike : AnswerTagItem=
      {
        atag:'',
        lbl:this.answersThree
      }
      ansLikes.push(ansLike)
      dataSave.ans = ansLikes;
    }
    else if(this.optionLst[3].checked){
      dataSave.type = "フリーワードと回数を指定する";
      dataSave.elapsedTimes = this.elapsedTimeData;
    } 
    else
    {
      dataSave.ans=[]
      dataSave.type=''
      dataSave.value=''
    }
    typeLst.forEach((t: any) =>{if( t.type == dataSave.type) dataSave.value = t.value; })
    if(!this.answerLst.some(answer => answer.selected) && this.optionLst[0].checked){
      dataSave.type='';
      dataSave.value='';
    }
    this.onSavedAnswerDialog.emit(dataSave ?? null);
  }
  closeModal(){ 
    const ref = this.dialogService.open(ConfirmDialogComponent, {
          width: '25%',
          height: '30%',
          data: {
            message: TITLE_SETTINGS.CANCEL_DIALOG,
            isOkCancel: true
          }
        })
    
        ref.onClose.subscribe(async (result) => {
          if (result === 'ok') {
              this.logDisplayAnswerSettingClosed();
              this.onClosedHandler.emit();
          }
        });
  }

  addElapsedTime(){ 
    if (this.elapsedTimeData?.length < this.elapsedMaxLength) {
      const timeElapsed = new TimeElapsedModel();
      timeElapsed.id = uuidv4();
      this.elapsedTimeData.push(timeElapsed);
    }
  }

  deleteElapsedTimeRow(timeData: TimeElapsedModel){ 
    this.elapsedTimeData = this.elapsedTimeData.filter(t => t.id !== timeData.id);
   }

  displayExistingAnswers(condition: any) {
    const parsedTag = this.getParsedTag(condition);
    const existingLbl = condition.val;

    this.optionLst.forEach(x => (x.checked = false));

    switch (condition.comparison) {

      case 'equal':
        this.optionLst[0].checked = true;
        this.optionOneSelected = this.getTypeLabel(condition.comparison);
        this.applySelectedAnswers(parsedTag,existingLbl);
        break;
      case 'not_equal':
      case 'answer_tag_like':
        this.optionLst[0].checked = true;
        this.optionOneSelected = this.getTypeLabel(condition.comparison);
        this.applySelectedAnswers(parsedTag,existingLbl);
        this.showOptions();
        break;

      case 'has_answer':
        this.optionLst[1].checked = true;
        this.showOptions();
        break;

      case 'like':
      case 'not_like':
        this.optionLst[2].checked = true;
        this.optionThreeSelected = this.getTypeLabel(condition.comparison);
        this.answersThree = condition.val;
        this.showOptions();
        break;
      case 'lst_compare':
        this.optionLst[3].checked = true;
        this.elapsedTimeData = JSON.parse(condition.val)
        this.isDisableElapsedTime=false;
        this.showOptions();
        break;

      default:
        break;
    }
  }

  private getTypeLabel(value: string): string {
    return typeLst.find(x => x.value === value)?.type ?? '';
  }

  private showOptions() {
    this.isDiableOption = false;
    this.isShowOptionSelect = true;
    this.symbolShowOptionSelect = '-';
  }

  private applySelectedAnswers(parsedTag: any[], selectedLbl: any) {
    this.answerLst = this.getSelectedAnswers(parsedTag, selectedLbl);
  }

  private clearOption(){
    this.optionLst.forEach(x => (x.checked = false));
  }

  private getParsedTag(condition: any): any[] {
    const parsedCondition =
      typeof condition?.tag === 'string'
        ? JSON.parse(condition.tag)
        : condition?.tag;

    return parsedCondition?.answerTags ?? [];
  }

  private hasSelectedAnswers(parsedTag: any[], selectedLbl: any): boolean {
    const selectedAnswers = this.getSelectedAnswers(parsedTag, selectedLbl);
    return selectedAnswers.some(x => x.selected === 'selected');
  }

  private getSelectedAnswers(parsedTag: any[], selectedLbl: any): AnswerTagItem[] {
    let selectedAnswers = this.buildSelectedAnswersByAtag(parsedTag);

    const hasSelected = selectedAnswers.some(x => x.selected === 'selected');

    if (!hasSelected && selectedLbl) {
      selectedAnswers = this.buildSelectedAnswersByLbl(selectedLbl);
    }

    return selectedAnswers;
  }

  private buildSelectedAnswersByAtag(parsedTag: any[]): AnswerTagItem[] {
    const selectedAtagSet = new Set(parsedTag.map(x => x.atag));

    return this.answerLst.map(x => ({
      ...x,
      selected: selectedAtagSet.has(x.atag) ? 'selected' : null
    }));
  }

  private buildSelectedAnswersByLbl(selectedLbl: any): AnswerTagItem[] {
    const selectedLblSet = new Set(
      (selectedLbl ?? '')
        .split(';')
        .map((x: string) => x.trim())
        .filter((x: string) => x)
    );

    return this.answerLst.map(x => ({
      ...x,
      selected: selectedLblSet.has(x.lbl) ? 'selected' : null
    }));
  }

  // SaucerLog [1/6]: 閲覧（View）
  private logDisplayAnswerSettingView(): void {
    try {
      const action = SAUCER_LOG_ACTION.TITLE_LINK_DISPLAY_ANSWER_SETTING.VIEW;
      const content = { title: this.getDisplayAnswerSettingTitle() };
      void this.saucerLogService.action({ content: JSON.stringify(content) }, { action }).catch(() => undefined);
    } catch {
      // SaucerLog failures must not interrupt the calling screen.
    }
  }

  private logDisplayAnswerSettingClosed(): void {
    try {
      const action = SAUCER_LOG_ACTION.TITLE_LINK_DISPLAY_ANSWER_SETTING.CLOSE_DIALOG;
      const content = { title: this.getDisplayAnswerSettingTitle() };
      void this.saucerLogService.action({ content: JSON.stringify(content) }, { action }).catch(() => undefined);
    } catch {
      // SaucerLog failures must not interrupt the calling screen.
    }
  }

  private getDisplayAnswerSettingTitle(): string {
    return `${this.title?.displayname} (${this.title?.colname})`;
  }
}
